import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { ResolverTag } from '@ps/web-core-enums';
import type { Modal } from '@ps/web-core-store';
import { tryParse } from '@ps/web-core-utils';
import { CashierPath } from '@ps/web-pam-types';
import { gameEntity } from '@ps/web-shared-analytics';
import type { History } from 'history';
import URL, { qs } from 'url-parse';

import type { CasinoLaunchPayload } from '@swf/components/CasinoGameLaunch/CasinoGameLaunch';
import { zoneStyles } from '@swf/components/StarsCrm/constants';
import { dispatch, getState, select } from '@swf/core/store';
import type { CasinoTable } from '@swf/core/types/CasinoGame';
import { closeWindow, getWindow, launchGame, searchRequest } from '@swf/core/utils/casino';

import type { Handler, HandlerProps, Path } from './matchRoute';
// TODO refactor away from central dispatch
const enqueue = (modal: Modal) => {
  dispatch.modal.close('/');
  dispatch.modal.enqueue(modal);
};

const nameLookup = new Map([
  ['cardtable', 'card-and-table'],
  ['gameshows', 'game-shows'],
  ['vegasslots', 'vegas'],
  ['baccaratandsicbo', 'baccarat']
]);

const modalHandler = (urlPath: string, history: History) => {
  const state = getState();

  if (select.location.isEmbeddedModal(state)) {
    history.push(urlPath);
  } else {
    enqueue({ path: urlPath });
  }
};

const getName = (name: string) => nameLookup.get(name) || name;

export const pageHandler: Handler = ({ path: { params }, history, tracker }) => {
  tracker?.('navigate');
  const { name, tab } = params;
  history.push({
    pathname: `/${getName(name) || ''}${tab ? `/${getName(tab)}/` : '/'}`
  });
};

export const signupHandler: Handler = ({ history, tracker }) => {
  tracker?.('sign_up');
  history.push(CashierPath.Signup);
};

export const authHandler: Handler = ({ tracker }) => {
  tracker?.('login');
  enqueue({ path: '/auth/login' });
};

export const modalZoneHandler: Handler = ({ path: { params }, tracker }) => {
  const { zone } = params;
  if (zone) {
    tracker?.('open_crm_modal');
    enqueue({
      path: '/stars-crm',
      locationState: {
        zoneId: zone.toUpperCase(),
        scrolling: 'yes',
        className: zoneStyles.MODALZONE,
        componentType: 'Single'
      }
    });
  }
};

const authModalHandler = (urlPath: string, history: History) => {
  const state = getState();
  const authenticated = select.session.isAuthenticated(state);
  const path = authenticated ? urlPath : '/auth/login';

  modalHandler(path, history);
};

export const accountHandler: Handler = ({ path: { url: urlPath }, history, tracker }) => {
  tracker?.('open_account');
  authModalHandler(urlPath, history);
};

export const prhubHandler: Handler = ({ path: { url: urlPath }, history }) => {
  modalHandler(urlPath, history);
};

export const gameLaunchHandler: Handler = async ({ path: { params }, history, tracker }) => {
  const { gameTableId, name } = params;
  if (!gameTableId) {
    return;
  }
  if (name && history) {
    pageHandler({ path: { params } as Path, history });
  }
  let windowRef;
  const rootState = getState();
  if (select.session.isAuthenticated(getState())) {
    const isMobile = select.device.isMobile(rootState);
    const isEmbedded = select.device.isEmbedded(rootState);
    windowRef = getWindow({
      table: { id: parseInt(gameTableId, 10) } as CasinoTable,
      isMobile,
      isEmbedded
    });
  }
  const results = await searchRequest({ query: gameTableId, paramCode: 'tabletypeid' }, rootState);

  if (results.length) {
    const [game] = results;
    if (game.tables.length === 1) {
      tracker?.('launch_game', [gameEntity(game)]);
      dispatch.casino.launch({ game, table: game.tables[0], windowRef });
    }
  } else {
    closeWindow(windowRef, select.device.isMobile(rootState));
  }
};

export const gameInternalLaunchHandler: Handler = ({ path: { params } }) => {
  const id = params.type === 'race' ? params.id : (tryParse(params.id) as string | undefined);
  const { code, raceId, fsb } = qs.parse(params[1]) as CasinoLaunchPayload;
  const race = params.type === 'race';
  launchGame({ id, code, raceId, race, fsb });
};

export const casinoC2LHandler: Handler = ({ path, history }) => {
  history.push(`/casino/${path.url}`);
};

export const casinoSOTDHandler: Handler = async () => {
  if (select.session.isAuthenticated(getState())) {
    await dispatch.casino.set({ shouldLaunchSotd: true });
  }
};

export const wslHandler: Handler = ({ path, history }) =>
  path.params.gameTableId ? gameLaunchHandler({ path, history }) : pageHandler({ path, history });

export const wslC2LRedirect = (resolverTag: ResolverTag) => {
  const state = getState();
  const wslNavigate: Handler = async ({
    path: {
      params: { basepath, route }
    },
    tracker,
    navigate
  }: HandlerProps) => {
    tracker?.('navigate');
    dispatch.modal.close();
    /* eslint prefer-const: ["error", {"destructuring": "all"}] */
    let tag = select.resolver.getTag(getState(), resolverTag);
    if (!tag) {
      await dispatch.resolver.fetchTag({ tag: resolverTag });
      tag = select.resolver.getTag(getState(), resolverTag);
    }
    let { url } = tag;
    let { host, pathname } = new URL(url);
    const activeHost = select.location.getHost(state);
    const isSports = select.cms.getBrandId(state) === BrandTypes.BET_STARS;
    if (basepath === 'sports' && host.includes(activeHost) && isSports && route?.length > 0) {
      window.location.hash = route;
      return;
    }
    if (resolverTag === ResolverTag.StarsWebSports && route?.length > 0) {
      const urlFragment = basepath?.includes('sportsisp') ? '' : '#/';
      const separator = url.endsWith('/') ? '' : '/';
      url = `${url}${separator}${urlFragment}${route}`;
      pathname = `${pathname}${separator}${urlFragment}${route}`;
    }
    if (host.includes(activeHost)) {
      const baseURL = select.location.getBaseURL(state);
      pathname = pathname.replace(new RegExp(`^${baseURL}`), '');
      navigate?.(pathname);
    } else {
      window.location.href = url;
    }
  };
  return wslNavigate;
};

export const launchICEHandler = (icegame: string, { params }: Path, history: History) => {
  const id = params.id ?? params.promoid;
  const { trigger } = params;

  history.push(`/account/ice/${icegame}/${id}${trigger ? `/${trigger}` : ''}`);
};

export const openWindowForTag = async (resolverTag: ResolverTag) => {
  await dispatch.resolver.fetchTag({ tag: resolverTag });
  const rootState = getState();
  const { url } = select.resolver.getTag(rootState, resolverTag);
  const { locale } = rootState;
  const parsed = new URL(url, true);
  parsed.query.language = locale.toString();
  window.open(parsed.toString());
};

export const launchWindowHandler: Handler = ({
  path: {
    params: { resolverTag }
  }
}) => openWindowForTag(resolverTag as ResolverTag);
